import { AppError, IdSchema } from "@hisani/core";
import { z } from "zod";
import { AUTH_ERROR_CODES, CognitoUsernameSchema } from "./service.js";
const AuthIdentityCommandDispatchSchema = z.object({ id: IdSchema }).strict();
const AuthIdentityCommandPublishResultSchema = z
    .object({
    acceptedIds: z.array(IdSchema),
    permanentFailureCount: z.number().int().nonnegative(),
    retryableFailureCount: z.number().int().nonnegative(),
})
    .strict();
const AuthIdentityAccountDisableResultSchema = z.discriminatedUnion("status", [
    z.object({ status: z.literal("DISABLED") }).strict(),
    z.object({ status: z.literal("NOT_APPLICABLE") }).strict(),
    z
        .object({
        status: z.literal("FAILED"),
        category: z.enum([
            "PERMANENT_PROVIDER_FAILURE",
            "RETRYABLE_PROVIDER_FAILURE",
        ]),
    })
        .strict(),
]);
const AuthIdentityCommandClaimResultSchema = z.discriminatedUnion("status", [
    z
        .object({
        status: z.literal("ACTIONABLE"),
        command: z
            .object({ id: IdSchema, cognitoUsername: CognitoUsernameSchema })
            .strict(),
    })
        .strict(),
    z.object({ status: z.literal("OBSOLETE"), id: IdSchema }).strict(),
    z.object({ status: z.literal("COMPLETED"), id: IdSchema }).strict(),
    z.object({ status: z.literal("NOT_FOUND") }).strict(),
]);
export async function dispatchAuthIdentityCommands(input, dependencies) {
    const { limit } = z
        .object({ limit: z.number().int().min(1).max(10).default(10) })
        .strict()
        .parse(input);
    try {
        const ready = z.boolean().parse(await dependencies.repository.isDispatchSchemaReady({
            system: "AUTH_IDENTITY_COMMAND_DISPATCHER",
        }));
        if (!ready) {
            return { outcome: "NOT_READY", pendingCount: 0, publishedCount: 0 };
        }
        const pending = z
            .array(AuthIdentityCommandDispatchSchema)
            .max(limit)
            .parse(await dependencies.repository.listPending({
            system: "AUTH_IDENTITY_COMMAND_DISPATCHER",
            limit,
        }));
        if (new Set(pending.map(({ id }) => id)).size !== pending.length) {
            throw authIdentityCommandUnavailable();
        }
        if (pending.length === 0) {
            return { outcome: "DISPATCHED", pendingCount: 0, publishedCount: 0 };
        }
        const result = validatePublishResult(pending, await dependencies.publisher.publish(pending));
        let publishedCount = 0;
        if (result.acceptedIds.length > 0) {
            const markedIds = z
                .array(IdSchema)
                .max(result.acceptedIds.length)
                .parse(await dependencies.repository.markPublished({
                system: "AUTH_IDENTITY_COMMAND_DISPATCHER",
                ids: result.acceptedIds,
                publishedAt: dependencies.clock.now(),
            }));
            const acceptedIds = new Set(result.acceptedIds);
            if (new Set(markedIds).size !== markedIds.length ||
                markedIds.some((id) => !acceptedIds.has(id))) {
                throw authIdentityCommandUnavailable();
            }
            publishedCount = markedIds.length;
        }
        if (result.permanentFailureCount > 0 || result.retryableFailureCount > 0) {
            throw authIdentityCommandUnavailable({
                category: result.permanentFailureCount > 0
                    ? "PERMANENT_PUBLICATION_FAILURE"
                    : "RETRYABLE_PUBLICATION_FAILURE",
                acceptedCount: result.acceptedIds.length,
                permanentFailureCount: result.permanentFailureCount,
                retryableFailureCount: result.retryableFailureCount,
            });
        }
        return {
            outcome: "DISPATCHED",
            pendingCount: pending.length,
            publishedCount,
        };
    }
    catch (cause) {
        if (cause instanceof AppError)
            throw cause;
        throw authIdentityCommandUnavailable(cause);
    }
}
export async function consumeAuthIdentityCommand(input, dependencies) {
    const { commandId } = z.object({ commandId: IdSchema }).strict().parse(input);
    try {
        const claim = AuthIdentityCommandClaimResultSchema.parse(await dependencies.repository.claimAttempt({
            system: "AUTH_IDENTITY_COMMAND_CONSUMER",
            id: commandId,
            attemptedAt: dependencies.clock.now(),
        }));
        switch (claim.status) {
            case "NOT_FOUND":
                return { outcome: "NOT_FOUND" };
            case "COMPLETED":
                return { outcome: "COMPLETED" };
            case "OBSOLETE":
                await complete(claim.id, dependencies);
                return { outcome: "OBSOLETE" };
            case "ACTIONABLE": {
                const disableResult = AuthIdentityAccountDisableResultSchema.parse(await dependencies.disabler.disableAccount(claim.command.cognitoUsername));
                if (disableResult.status === "FAILED") {
                    throw authIdentityCommandUnavailable({
                        category: disableResult.category,
                    });
                }
                await complete(claim.command.id, dependencies);
                return { outcome: disableResult.status };
            }
        }
    }
    catch (cause) {
        if (cause instanceof AppError)
            throw cause;
        throw authIdentityCommandUnavailable(cause);
    }
}
export function authIdentityCommandDispatchFailureEvidence(error) {
    if (!(error instanceof AppError))
        return null;
    const parsed = z
        .object({
        category: z.enum([
            "PERMANENT_PUBLICATION_FAILURE",
            "RETRYABLE_PUBLICATION_FAILURE",
        ]),
        acceptedCount: z.number().int().nonnegative(),
        permanentFailureCount: z.number().int().nonnegative(),
        retryableFailureCount: z.number().int().nonnegative(),
    })
        .strict()
        .safeParse(error.cause);
    return parsed.success ? parsed.data : null;
}
export function authIdentityCommandConsumptionFailureEvidence(error) {
    if (!(error instanceof AppError))
        return null;
    const parsed = z
        .object({
        category: z.enum([
            "PERMANENT_PROVIDER_FAILURE",
            "RETRYABLE_PROVIDER_FAILURE",
        ]),
    })
        .strict()
        .safeParse(error.cause);
    return parsed.success ? parsed.data : null;
}
function validatePublishResult(pending, candidate) {
    const parsed = AuthIdentityCommandPublishResultSchema.safeParse(candidate);
    if (!parsed.success)
        throw authIdentityCommandUnavailable(parsed.error);
    const pendingIds = new Set(pending.map(({ id }) => id));
    const acceptedIds = new Set(parsed.data.acceptedIds);
    if (acceptedIds.size !== parsed.data.acceptedIds.length ||
        parsed.data.acceptedIds.some((id) => !pendingIds.has(id)) ||
        parsed.data.acceptedIds.length +
            parsed.data.permanentFailureCount +
            parsed.data.retryableFailureCount !==
            pending.length) {
        throw authIdentityCommandUnavailable();
    }
    return parsed.data;
}
async function complete(id, dependencies) {
    const completed = z.boolean().parse(await dependencies.repository.markCompleted({
        system: "AUTH_IDENTITY_COMMAND_CONSUMER",
        id,
        completedAt: dependencies.clock.now(),
    }));
    if (!completed) {
        throw authIdentityCommandUnavailable();
    }
}
function authIdentityCommandUnavailable(cause) {
    return new AppError({
        code: AUTH_ERROR_CODES.identitySyncUnavailable,
        message: "Authentication identity synchronization is unavailable.",
        ...(cause === undefined ? {} : { cause }),
    });
}
